import { z } from 'zod/v3';
import { ComplianceExceptionDTOSchema } from './compliance-exception.dto';

export const ScanPhaseSchema = z.enum([
  'idle',
  'queued',
  'clone',
  'analysis',
  'complete',
  'failed',
  'skipped',
]);

export const ScanLevelSchema = z.enum([
  'pass',
  'warn',
  'fail',
  'info',
  'progress',
]);

export const EnrichedFindingMetaDTOSchema = z.object({
  controlDefId: z.string(),
  screenataControlId: z.string(),
  controlTitle: z.string(),
  templateCount: z.number(),
  templateNames: z.array(z.string()),
});

export const ScanFindingEventDTOSchema = z.object({
  id: z.string(),
  seq: z.number().int().nonnegative(),
  phase: ScanPhaseSchema,
  level: ScanLevelSchema,
  message: z.string(),
  checkId: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
  timestamp: z.string(),
  enrichment: z.array(EnrichedFindingMetaDTOSchema).optional(),
}).strict();

export const ScanFindingsSummaryDTOSchema = z.object({
  passedChecks: z.number().int().nonnegative(),
  reviewChecks: z.number().int().nonnegative(),
  failedChecks: z.number().int().nonnegative(),
  totalChecks: z.number().int().nonnegative(),
  /**
   * How many checks the run intends to execute end-to-end. Populated from
   * a progress event emitted at scan kickoff; null until the first event
   * lands. Useful for "Scanning X of Y" progress UI where Y is the plan,
   * not the completed count.
   */
  plannedTotal: z.number().int().nonnegative().nullable(),
  /**
   * How many checks have actually finished running so far — tracked as a
   * running counter via per-check progress events. Distinct from
   * `totalChecks`, which only counts checks that produced a pass/warn/fail
   * finding (mid-scan, totalChecks stays at 0 until persistQuickFindings
   * lands the authoritative findings at the end).
   */
  scannedCount: z.number().int().nonnegative().nullable(),
  reportAvailable: z.boolean(),
}).strict();

export const ScanFindingsResponseDTOSchema = z.object({
  runId: z.string().nullable(),
  phase: ScanPhaseSchema,
  percent: z.number().int().min(0).max(100),
  isTerminal: z.boolean(),
  findings: z.array(ScanFindingEventDTOSchema),
  summary: ScanFindingsSummaryDTOSchema,
}).strict();

export type ScanPhase = z.infer<typeof ScanPhaseSchema>;
export type ScanLevel = z.infer<typeof ScanLevelSchema>;
export type ScanFindingEventDTO = z.infer<typeof ScanFindingEventDTOSchema>;
export type ScanFindingsSummaryDTO = z.infer<typeof ScanFindingsSummaryDTOSchema>;
export type ScanFindingsResponseDTO = z.infer<typeof ScanFindingsResponseDTOSchema>;

// ---------------------------------------------------------------------------
// Enablement preview — "what does this connection unlock?"
// ---------------------------------------------------------------------------

export const SlotCoverageEntryDTOSchema = z.object({
  slotKey: z.string(),
  description: z.string(),
  checkCount: z.number().int().nonnegative(),
  testCount: z.number().int().nonnegative(),
  alreadyEnabled: z.boolean(),
});

export const EnablementPreviewDTOSchema = z.object({
  availableCheckCount: z.number().int().nonnegative(),
  coveredSlotKeys: z.array(z.string()),
  materializableTestCount: z.number().int().nonnegative(),
  alreadyMaterializedTestCount: z.number().int().nonnegative(),
  slots: z.array(SlotCoverageEntryDTOSchema),
  testsByFramework: z.record(z.number().int().nonnegative()).optional(),
});

export type SlotCoverageEntryDTO = z.infer<typeof SlotCoverageEntryDTOSchema>;
export type EnablementPreviewDTO = z.infer<typeof EnablementPreviewDTOSchema>;

// ---------------------------------------------------------------------------
// Posture score — derived from latest scan findings
// ---------------------------------------------------------------------------

export const ConnectionPostureDTOSchema = z.object({
  connectionId: z.string(),
  provider: z.string(),
  label: z.string(),
  passed: z.number().int().nonnegative(),
  warnings: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  totalChecks: z.number().int().nonnegative(),
  lastScannedAt: z.string().nullable(),
});

export const PostureScoreDTOSchema = z.object({
  score: z.number().min(0).max(100),
  connections: z.array(ConnectionPostureDTOSchema),
  totalSlotsCovered: z.number().int().nonnegative(),
  totalSlotsNeeded: z.number().int().nonnegative(),
  lastScannedAt: z.string().nullable(),
});

export type ConnectionPostureDTO = z.infer<typeof ConnectionPostureDTOSchema>;
export type PostureScoreDTO = z.infer<typeof PostureScoreDTOSchema>;

export const GitHubRepoLatestScanDTOSchema = z.object({
  repoFullName: z.string(),
  runId: z.string().nullable(),
  phase: ScanPhaseSchema,
  isTerminal: z.boolean(),
  startedAt: z.string().nullable(),
  scopeRepoCount: z.number().int().nonnegative().nullable(),
  summary: ScanFindingsSummaryDTOSchema.nullable(),
}).strict();

export type GitHubRepoLatestScanDTO = z.infer<
  typeof GitHubRepoLatestScanDTOSchema
>;

export const CloudConnectionLatestScanDTOSchema = z.object({
  connectionId: z.string(),
  runId: z.string().nullable(),
  phase: ScanPhaseSchema,
  isTerminal: z.boolean(),
  startedAt: z.string().nullable(),
  summary: ScanFindingsSummaryDTOSchema.nullable(),
}).strict();

export type CloudConnectionLatestScanDTO = z.infer<
  typeof CloudConnectionLatestScanDTOSchema
>;

export const CloudConnectionRunDTOSchema = z.object({
  runId: z.string(),
  phase: ScanPhaseSchema,
  isTerminal: z.boolean(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  mode: z.string(),
  scanKind: z.string().nullable(),
  summary: ScanFindingsSummaryDTOSchema,
}).strict();

export type CloudConnectionRunDTO = z.infer<typeof CloudConnectionRunDTOSchema>;

// ---------------------------------------------------------------------------
// Provider checks list — pre-scan view of available checks + last status
// ---------------------------------------------------------------------------

export const ProviderCheckLastResultSchema = z.enum([
  'pass',
  'fail',
  'warn',
  'info',
  'never_run',
  'error',
  // Last scan failed (e.g. background task stalled past the zombie threshold)
  // before this check emitted any finding. Distinct from `never_run` so the UI
  // can tell "queued but cut short" apart from "catalog had no such check at
  // the time the last scan ran".
  'interrupted',
]);

/**
 * RFC-216 M0B — one AI-governance citation on a check, resolved server-side.
 *
 * Separate from `controlMappings` on purpose: those are canonical SC-* control
 * refs used for attachment and search, while these are display-only citations
 * into the AI catalogs (`config/compliance/frameworks/ai/`). Sent pre-resolved
 * so the client renders a labelled chip without shipping the catalogs.
 */
export const AIGovernanceCitationDTOSchema = z.object({
  /** Catalog key, e.g. "ISO42001:A.9.4". */
  fullId: z.string(),
  /** Requirement id within its framework, e.g. "A.9.4". */
  id: z.string(),
  /** Framework display name, e.g. "ISO/IEC 42001". */
  frameworkLabel: z.string(),
  /** Short functional title, e.g. "Intended use". */
  title: z.string(),
});
export type AIGovernanceCitationDTO = z.infer<typeof AIGovernanceCitationDTOSchema>;

export const ProviderCheckRunResultSchema = z.enum([
  'running',
  'error',
  'pass',
  'fail',
  'review',
  'not_applicable',
  'skipped',
  'interrupted',
]);

export const ProviderCheckRunSummaryDTOSchema = z.object({
  runId: z.string(),
  result: ProviderCheckRunResultSchema,
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  resultCount: z.number().int().nonnegative(),
  passedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  reviewCount: z.number().int().nonnegative(),
  exceptedCount: z.number().int().nonnegative(),
  skipReason: z.string().nullable(),
  errorMessage: z.string().nullable(),
}).strict();

export const ProviderCheckSummaryDTOSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  /** Provider service this check belongs to when the registry defines one. */
  resourceServiceId: z.string().nullable(),
  /** Excluded by this connection's saved service usage; independent of last result. */
  disabledByServiceScope: z.boolean(),
  controlMappings: z.array(z.string()),
  /** RFC-216 M0B — AI-governance citations; empty for checks with no AI dimension. */
  aiControlMappings: z.array(AIGovernanceCitationDTOSchema),
  slotKeys: z.array(z.string()),
  lastResult: ProviderCheckLastResultSchema,
  lastRunSummary: ProviderCheckRunSummaryDTOSchema.nullable(),
  lastRunAt: z.string().nullable(),
  lastRunId: z.string().nullable(),
  /** When lastResult is fail/warn, count of failing/warning items. */
  lastFailedCount: z.number().int().nonnegative(),
  /** First failed/warning finding's title — used to seed the "Fix it" chat context. */
  lastFailureTitle: z.string().nullable(),
  /** Concatenated remediation hint from the latest failed/warning finding(s). */
  lastFailureRemediation: z.string().nullable(),
});

export const ProviderChecksListDTOSchema = z.object({
  provider: z.string(),
  checks: z.array(ProviderCheckSummaryDTOSchema),
});

export type ProviderCheckLastResult = z.infer<
  typeof ProviderCheckLastResultSchema
>;
export type ProviderCheckSummaryDTO = z.infer<
  typeof ProviderCheckSummaryDTOSchema
>;
export type ProviderChecksListDTO = z.infer<typeof ProviderChecksListDTOSchema>;

export const ScanFindingStatusSchema = z.enum(['pass', 'fail', 'warn', 'info']);
export const ResourceScopeStateSchema = z.enum([
  'IN_SCOPE',
  'OUT_OF_SCOPE',
  'PARTIAL',
]);

export const ScanCheckFindingDTOSchema = z.object({
  id: z.string(),
  checkId: z.string(),
  resourceId: z.string().nullable(),
  resourceType: z.string().nullable(),
  status: ScanFindingStatusSchema,
  severity: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  remediation: z.string().nullable(),
  evidence: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
  /** Active exception covering this finding, if any. */
  exception: ComplianceExceptionDTOSchema.nullable(),
});

export type ScanFindingStatus = z.infer<typeof ScanFindingStatusSchema>;
export type ScanCheckFindingDTO = z.infer<typeof ScanCheckFindingDTOSchema>;

export const ProviderCheckRunHistoryDTOSchema = z.object({
  runs: z.array(ProviderCheckRunSummaryDTOSchema),
}).strict();

export const ProviderCheckRunDetailDTOSchema = z.object({
  run: ProviderCheckRunSummaryDTOSchema,
  findings: z.array(ScanCheckFindingDTOSchema),
}).strict();

export type ProviderCheckRunResult = z.infer<typeof ProviderCheckRunResultSchema>;
export type ProviderCheckRunSummaryDTO = z.infer<typeof ProviderCheckRunSummaryDTOSchema>;
export type ProviderCheckRunHistoryDTO = z.infer<typeof ProviderCheckRunHistoryDTOSchema>;
export type ProviderCheckRunDetailDTO = z.infer<typeof ProviderCheckRunDetailDTOSchema>;

export const ServiceResourceCheckDTOSchema = z.object({
  resourceId: z.string(),
  checkId: z.string(),
  checkName: z.string(),
  status: ScanFindingStatusSchema,
  severity: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  remediation: z.string().nullable(),
  runId: z.string(),
  runStartedAt: z.string(),
  exception: ComplianceExceptionDTOSchema.nullable(),
}).strict();

export const ServiceResourceDTOSchema = z.object({
  resourceId: z.string(),
  resourceType: z.string().nullable(),
  displayName: z.string(),
  region: z.string().nullable(),
  sources: z.array(z.enum(['DISCOVERY', 'SCAN'])),
  scopeResourceIds: z.array(z.string()),
  scopeState: ResourceScopeStateSchema,
  checks: z.array(ServiceResourceCheckDTOSchema),
}).strict();

export const ServiceResourcesResponseDTOSchema = z.object({
  serviceId: z.string(),
  inventoryState: z.enum(['NOT_DETECTED', 'COMPLETE', 'PARTIAL', 'FAILED']),
  resources: z.array(ServiceResourceDTOSchema),
}).strict();

export type ServiceResourceDTO = z.infer<typeof ServiceResourceDTOSchema>;
export type ServiceResourcesResponseDTO = z.infer<typeof ServiceResourcesResponseDTOSchema>;

// ---------------------------------------------------------------------------
// Streaming agent tool chunks — RFC-176 PR 2
// `checks.scan` (and any future streaming scan tool) yields these chunks
// over the AI SDK 6 preliminary-tool-results channel. The last yielded
// chunk in any successful run is `complete`; on failure it is `failed`.
// ---------------------------------------------------------------------------

const ScanProgressStartedChunkSchema = z.object({
  type: z.literal('started'),
  runId: z.string(),
  provider: z.string(),
  plannedTotal: z.number().int().nonnegative(),
  reusedExistingRun: z.boolean(),
});

const ScanProgressProgressChunkSchema = z.object({
  type: z.literal('progress'),
  runId: z.string(),
  checkId: z.string().optional(),
  scannedCount: z.number().int().nonnegative(),
  plannedTotal: z.number().int().nonnegative(),
  level: ScanLevelSchema.optional(),
});

const ScanProgressCompleteChunkSchema = z.object({
  type: z.literal('complete'),
  runId: z.string(),
  provider: z.string(),
  phase: ScanPhaseSchema,
  summary: ScanFindingsSummaryDTOSchema,
  findings: z.array(ScanFindingEventDTOSchema),
});

const ScanProgressFailedChunkSchema = z.object({
  type: z.literal('failed'),
  runId: z.string(),
  provider: z.string(),
  reason: z.string(),
});

export const ScanProgressChunkDTOSchema = z.discriminatedUnion('type', [
  ScanProgressStartedChunkSchema,
  ScanProgressProgressChunkSchema,
  ScanProgressCompleteChunkSchema,
  ScanProgressFailedChunkSchema,
]);

export type ScanProgressChunkDTO = z.infer<typeof ScanProgressChunkDTOSchema>;
